import React from "react";
import {Button} from "@/components/buttons/Button";
import { MdCheck } from "react-icons/md";
import classNames from "classnames";
import { isDesktop } from "react-device-detect";
import useOverwolfInterop from "@/hooks/useOverwolfInterop";

interface ClaimBadgeButtonProps {
    // Context about what we're claiming
    type: "quest" | "milestone";

    // IDs for claiming
    questId: string;
    milestoneId?: string;

    // State data
    isComplete: boolean;
    /**
     * @deprecated Use hasManualClaimRewards instead
     * True if milestone has rewardPer set (all rewards auto-claim)
     */
    isRewardPerMilestone?: boolean;
    /**
     * True if milestone has any rewards that require manual claiming
     * (rewardAutoSettings[id] === false OR no rewardPer and no explicit setting)
     */
    hasManualClaimRewards?: boolean;
    claimedAt?: Date | number;
    hasUnclaimedMilestones?: boolean;
    isCollapsed?: boolean;
    hasRootLevelRewards?: boolean;
    allMilestonesClaimed?: boolean;

    // Actions
    onClaim: (id: string) => Promise<void>;
    onUncollapseAndScroll?: (e: React.MouseEvent<HTMLAnchorElement>) => void;

    // UI state
    isClaimingInProgress: boolean;
    windowWidth: number;
    forceModalDesign?: boolean;

    // Style
    className?: string;
}

const MOBILE_BREAKPOINT = 576;
const MEDIUM_BREAKPOINT = 1280;
const LARGE_BREAKPOINT = 1650;

export default function ClaimBadgeButton({
    type,
    questId,
    milestoneId,
    isComplete,
    isRewardPerMilestone,
    hasManualClaimRewards,
    claimedAt,
    hasUnclaimedMilestones,
    isCollapsed,
    hasRootLevelRewards,
    allMilestonesClaimed,
    onClaim,
    onUncollapseAndScroll,
    isClaimingInProgress,
    windowWidth,
    forceModalDesign,
    className,
}: ClaimBadgeButtonProps) {
    const { isOnOverwolf } = useOverwolfInterop();
    const isMobileView = windowWidth <= MOBILE_BREAKPOINT || forceModalDesign;
    const isMediumBreakpoint = windowWidth >= MEDIUM_BREAKPOINT && windowWidth < LARGE_BREAKPOINT;

    /*
     * Determine if there are manual claim rewards
     * New prop takes priority, fallback to inverse of legacy isRewardPerMilestone
     */
    const shouldShowClaimButton = hasManualClaimRewards !== undefined
        ? hasManualClaimRewards
        : !isRewardPerMilestone;

    // Determine what to render based on state
    const determineRenderState = (): "claim" | "claimed" | "all-claimed" | "uncollapse" | "none" => {
        // If already claimed, show claimed badge
        if (claimedAt) {
            return "claimed";
        }

        // If this is a quest-level button
        if (type === "quest") {
            // If there are unclaimed milestones and we're collapsed, show uncollapse button
            if (hasUnclaimedMilestones && isCollapsed) {
                return "uncollapse";
            }
            // If there are unclaimed milestones and we're not collapsed, don't show anything
            if (hasUnclaimedMilestones && !isCollapsed) {
                return "none";
            }
            // If quest is complete and no unclaimed milestones and has root rewards, show claim
            if (isComplete && !hasUnclaimedMilestones && hasRootLevelRewards) {
                return "claim";
            }
            // If all milestones are claimed and no root level rewards, show "All Claimed"
            if (allMilestonesClaimed && !hasRootLevelRewards && !hasUnclaimedMilestones) {
                return "all-claimed";
            }
            return "none";
        }

        // For milestone-level buttons
        if (type === "milestone") {
            // If milestone is complete and has manual claim rewards, show claim button
            if (isComplete && shouldShowClaimButton) {
                return "claim";
            }
            // If complete but all rewards are auto-claimed, don't show claim button
            if (isComplete && !shouldShowClaimButton) {
                return "none";
            }
            return "none";
        }

        return "none";
    };

    const renderState = determineRenderState();

    if (renderState === "none") {
        return null;
    }

    // Render claimed badge
    if (renderState === "claimed") {
        const claimedDate = new Intl.DateTimeFormat("en-US", {
            month: "short",
            day: "numeric",
        }).format(new Date(claimedAt));

        // For milestones, don't show badge - status text already shows claimed date
        if (type === "milestone") {
            return null;
        }

        return (
            <Button variant="primary"
                className={classNames(
                    "go-vip-ow_quest-reward-card_claimed",
                    {
                        "desktop-claimed-btn": !isMobileView && !isMediumBreakpoint,
                        "mobile-claimed-btn": isMobileView || isMediumBreakpoint,
                    },
                    className,
                )}
            >
                Claimed on {claimedDate} {(isOnOverwolf || isDesktop) && <MdCheck />}
            </Button>
        );
    }

    // Render "All Claimed" badge
    if (renderState === "all-claimed") {
        return (
            <Button variant="primary"
                className={classNames(
                    "go-vip-ow_quest-reward-card_claimed",
                    {
                        "desktop-claimed-btn": !isMobileView && !isMediumBreakpoint,
                        "mobile-claimed-btn": isMobileView || isMediumBreakpoint,
                    },
                    className,
                )}
            >
                All Claimed {(isOnOverwolf || isDesktop) && <MdCheck />}
            </Button>
        );
    }

    // Render uncollapse button (for quest-level when milestones need claiming)
    if (renderState === "uncollapse") {
        return (
            <Button variant="primary"
                onClick={onUncollapseAndScroll}

                className={classNames(
                    "go-vip-ow_quest-reward-card_claim-btn",
                    {
                        "mobile-claim-btn": isMobileView || isMediumBreakpoint,
                    },
                    className,
                )}
            >
                Claim
            </Button>
        );
    }

    /*
     * Render regular claim button
     * Don't apply color prop if using custom className (e.g., cockpit styling)
     */
    const buttonProps: any = {
        size: "narrow",
        onClick: () => onClaim(milestoneId || questId),
        className: classNames(
            "go-vip-ow_quest-reward-card_claim-btn",
            {
                "mobile-claim-btn": isMobileView,
            },
            className,
        ),
        forwardProps: { disabled: isClaimingInProgress },
    };

    // Only apply default color if no custom className is provided
    if (!className) {
        buttonProps.color = "devourplay-gradient";
    }

    return (
        <Button variant="primary" {...buttonProps}>
            Claim
        </Button>
    );
}